import CONFIG from "@/config";
import ShowToaster from "@/toaster";
import tokenManager from "@/utils/tokenManager";
import { buildUrl } from "@/utils/url";
import { safeCaptureException } from "@/utils/sentry";
import ky from "ky";
import { ERROR_MESSAGE_SHOULD_BE_VISIBLE } from "./utils";
import ErrorCodes from "@/constants/errors/ErrorCodes";

const commonHooks = {
  beforeRequest: [
    (request: any): void => {
      const token = tokenManager.getToken();
      if (token) {
        request.headers.set("Authorization", token);
      }
    },
  ],

  beforeError: [
    async (error: any) => {
      const { response, request } = error;

      if (response && response.status === 401) {
        console.warn("Unauthorized request - token might be expired");
        tokenManager.removeToken();

        // redirect to login page
        const params = {
          domain: encodeURIComponent(window.location.origin),
          completeUrl: CONFIG.endPoints.auth0 + "/",
          failUrl: CONFIG.endPoints.auth0 + "/register/fail",
          serverLogin: "true",
        };
        const loginURL = buildUrl(
          CONFIG.endPoints.userService,
          "/auth/login",
          params
        );
        window.location.href = loginURL;
      } else {
        // Capture non-401 errors to Sentry
        const apiError = new Error(
          `API Error: ${response?.status || "Network Error"} - ${request?.url || "Unknown URL"}`
        );
        apiError.name = "APIError";

        // Get response body if available
        let responseBody: string | undefined;
        try {
          if (response) {
            const clonedResponse = response.clone();
            responseBody = await clonedResponse.text();
          }
        } catch {
          // Ignore errors reading response body
        }

        safeCaptureException(apiError, {
          tags: {
            error_type: "api_error",
            http_status: response?.status?.toString() || "unknown",
            http_method: request?.method || "unknown",
          },
          extra: {
            url: request?.url || "unknown",
            status: response?.status || "unknown",
            statusText: response?.statusText || "unknown",
            responseBody: responseBody?.substring(0, 1000) || "unavailable", // Limit response body size
            timestamp: new Date().toISOString(),
          },
        });
      }

      return error;
    },
  ],

  afterResponse: [
    async (request: any, options: any, response: any) => {
      try {
        const parsedResponse = await response.text();
        try {
          if (
            !parsedResponse ||
            (!parsedResponse.trim().startsWith("{") &&
              !parsedResponse.trim().startsWith("["))
          ) {
            return new Response(parsedResponse, response);
          }
          const responseJSON = JSON.parse(parsedResponse);
          if (responseJSON?.error && responseJSON?.error?.length > 0) {
            // Capture API response errors to Sentry
            const apiResponseError = new Error(
              `API Response Error: ${responseJSON?.error} - ${request?.url || "Unknown URL"}`
            );
            apiResponseError.name = "APIResponseError";

            safeCaptureException(apiResponseError, {
              tags: {
                error_type: "api_response_error",
                http_status: response?.status?.toString() || "unknown",
                http_method: request?.method || "unknown",
              },
              extra: {
                url: request?.url || "unknown",
                status: response?.status || "unknown",
                errorMessage: responseJSON?.error || "unknown",
                message: responseJSON?.message || "unknown",
                responseBody: parsedResponse?.substring(0, 1000) || "unavailable",
                timestamp: new Date().toISOString(),
              },
            });

            const errorCode = responseJSON.code as string;
            if (errorCode && ErrorCodes[errorCode as keyof typeof ErrorCodes]) {
              const error = ErrorCodes[errorCode as keyof typeof ErrorCodes];
              ShowToaster.error(error.code ? `${error.message}\nError code: ${error.code}` : error.message);
            } else if (ERROR_MESSAGE_SHOULD_BE_VISIBLE?.includes(responseJSON?.message)) {
              ShowToaster.error(responseJSON.code ? `${responseJSON?.message}\nError code: ${responseJSON.code}` : responseJSON?.message);
            } else if (ERROR_MESSAGE_SHOULD_BE_VISIBLE?.includes(responseJSON?.error)) {
              ShowToaster.error(responseJSON.code ? `${responseJSON?.error}\nError code: ${responseJSON.code}` : responseJSON?.error);
            } else if (response.status === 400) {
              ShowToaster.error(ErrorCodes["REQ-E-0001"].code ? `${ErrorCodes["REQ-E-0001"].message}\nError code: ${ErrorCodes["REQ-E-0001"].code}` : ErrorCodes["REQ-E-0001"].message);
            } else if (response.status === 500) {
              ShowToaster.error(ErrorCodes["SERVER-E-001"].code ? `${ErrorCodes["SERVER-E-001"].message}\nError code: ${ErrorCodes["SERVER-E-001"].code}` : ErrorCodes["SERVER-E-001"].message);
            } else {
              ShowToaster.error(ErrorCodes["UI-E-001"].code ? `${ErrorCodes["UI-E-001"].message}\nError code: ${ErrorCodes["UI-E-001"].code}` : ErrorCodes["UI-E-001"].message);
            }
            return new Response(null, response);
          }
          const data = responseJSON?.data || responseJSON || {};
          return new Response(JSON.stringify(data), response);
        } catch (err: any) {
          // Capture JSON parsing errors
          const parseError = new Error(
            `Failed to parse API response: ${request?.url || "Unknown URL"}`
          );
          parseError.name = "APIResponseParseError";
          safeCaptureException(parseError, {
            tags: {
              error_type: "api_parse_error",
              http_status: response?.status?.toString() || "unknown",
              http_method: request?.method || "unknown",
            },
            extra: {
              url: request?.url || "unknown",
              status: response?.status || "unknown",
              responsePreview: parsedResponse?.substring(0, 500) || "unavailable",
              timestamp: new Date().toISOString(),
            },
          });
          return new Response(parsedResponse, response);
        }
      } catch (err: any) {
        // Capture response reading errors
        const readError = new Error(
          `Failed to read API response: ${request?.url || "Unknown URL"}`
        );
        readError.name = "APIResponseReadError";
        safeCaptureException(readError, {
          tags: {
            error_type: "api_read_error",
            http_status: response?.status?.toString() || "unknown",
            http_method: request?.method || "unknown",
          },
          extra: {
            url: request?.url || "unknown",
            status: response?.status || "unknown",
            timestamp: new Date().toISOString(),
          },
        });
      }
    },
  ],
};

// // common retry config
// // will setup common retry config later
// const commonRetry = {
//     limit: 2,
//     methods: ['get'],
//     statusCodes: [408, 413, 429, 500, 502, 503, 504],
// }

// connection timeout configuration
const commonTimeout = 30000; //30 seconds

export const createAPIInstance = (baseURL: string, customOptions: any = {}) => {
  return ky.create({
    prefixUrl: baseURL,
    timeout: commonTimeout,
    hooks: commonHooks,
    retry: 0,
    ...customOptions,
  });
};

// pre-configured API instances for services
export const apiInstances = {
  user: createAPIInstance(CONFIG.endPoints.userService),
  data: createAPIInstance(CONFIG.endPoints.dataService),
  export: createAPIInstance(CONFIG.endPoints.exportService),
};
